/**
 * Geo policy admin panel — Task T2-C-web (Tier-2 ops, pillars-deepening program).
 * `web/shapes/geo.md` §7 is the agreed shape.
 *
 * T2-C shipped `GET/PUT /api/v1/geo/policy` — the check-in switch, the trust
 * thresholds `trust.ts` reads, and the outside-geofence enforcement mode — with no
 * web caller. This panel is that caller. `GeoScreen` renders it only when the
 * session's `permissions` includes `workforce.manage_policies`; the server is still
 * the authority, and a `403` on the read below (a session that believes it holds
 * the code and does not) is rendered through the ordinary `ApiErrorNote` path
 * rather than swallowed.
 *
 * ── The draft carries only what the person actually touched ─────────────────
 *
 * `draft` is a `GeoPolicyUpdateRequest`, not a full copy of the loaded `GeoPolicy`:
 * a field the person never touched is simply absent from it, and the value shown
 * on screen for that field falls back to the loaded policy (or, while it is still
 * loading, the same closed default `GET /geo/policy` itself answers with for a
 * company with no row). Pressing "Lưu chính sách" sends `draft` as-is — "changing
 * only the mode sends `{ outside_geofence_mode: 'block' }` and nothing else" is
 * true by construction, not by a diff computed at submit time. A successful save
 * clears `draft`, keyed on `saved` changing identity, the same "only a success
 * resets the form" instinct `PoliciesScreen`'s own `useEffect` documents.
 *
 * ── The trust thresholds never block anything ────────────────────────────────
 *
 * `max_accuracy_m` and `max_travel_kmh` are worded on screen exactly as
 * `trust.ts` behaves: crossing either threshold changes a stored check-in's
 * `trust` verdict, and nothing here is a refusal — the two helper sentences say so
 * so an administrator does not read this panel as a second geofence gate.
 */

import { useEffect, useState, type ReactNode } from 'react';

import { useI18n, type Locale } from '../../i18n';
import { formatInstant } from '../../i18n/datetime';
import type { GeoOutsideGeofenceMode, GeoPolicy, GeoPolicyUpdateRequest } from '../../api/geo';
import type { QueryStatus } from '../../api/useApiQuery';
import type { WorkforceApiError } from '../../api/errors';
import { ApiErrorNote } from '../../states/ApiErrorNote';
import { Skeleton } from '../../states/Skeleton';
import { StatusNote } from '../../states/StatusNote';

export interface GeoPolicyPanelProps {
  readonly status: QueryStatus;
  readonly policy: GeoPolicy | null;
  readonly error: WorkforceApiError | null;
  readonly onReload: () => void;
  readonly savePending: boolean;
  readonly saveError: WorkforceApiError | null;
  readonly saved: GeoPolicy | null;
  readonly onSave: (body: GeoPolicyUpdateRequest) => void;
  readonly locale: Locale;
  readonly timezone: string;
}

/** The closed defaults `GET /geo/policy` answers with for a company with no row. */
const DEFAULT_MAX_ACCURACY_M = 100;
const DEFAULT_MAX_TRAVEL_KMH = 900;
const DEFAULT_MODE: GeoOutsideGeofenceMode = 'allow_flag';

export function GeoPolicyPanel(props: GeoPolicyPanelProps): ReactNode {
  const { t } = useI18n();
  const [draft, setDraft] = useState<GeoPolicyUpdateRequest>({});

  // A successful save is the only thing that clears pending edits — see this
  // file's own header for why that must be keyed on `saved`'s identity rather than
  // computed from a diff.
  const { saved } = props;
  useEffect(() => {
    if (saved !== null) setDraft({});
  }, [saved]);

  const enabled = draft.geo_checkin_enabled ?? props.policy?.geo_checkin_enabled ?? false;
  const maxAccuracyM =
    draft.max_accuracy_m ?? props.policy?.max_accuracy_m ?? DEFAULT_MAX_ACCURACY_M;
  const maxTravelKmh =
    draft.max_travel_kmh ?? props.policy?.max_travel_kmh ?? DEFAULT_MAX_TRAVEL_KMH;
  const mode = draft.outside_geofence_mode ?? props.policy?.outside_geofence_mode ?? DEFAULT_MODE;
  const dirty = Object.keys(draft).length > 0;

  return (
    <section className="wp-section" aria-labelledby="w10-policy-heading" data-testid="w10-policy">
      <h2 className="wp-section__title" id="w10-policy-heading">
        {t('geo.policy.title')}
      </h2>

      {props.status === 'loading' ? (
        <Skeleton
          rows={3}
          variant="block"
          label={t('geo.policy.subject')}
          testId="w10-policy-skeleton"
        />
      ) : null}

      {props.status === 'error' && props.error !== null ? (
        <ApiErrorNote
          error={props.error}
          onRetry={props.onReload}
          subject={t('geo.policy.subject')}
          testId="w10-policy-error"
        />
      ) : null}

      {props.policy === null ? null : (
        <form
          className="wp-stack"
          data-testid="w10-policy-form"
          onSubmit={(event) => {
            event.preventDefault();
            if (!dirty || props.savePending) return;
            props.onSave(draft);
          }}
        >
          <div className="wp-filters__group">
            <label className="wp-toggle" htmlFor="w10-policy-enabled">
              <input
                id="w10-policy-enabled"
                type="checkbox"
                data-testid="w10-policy-enabled"
                checked={enabled}
                onChange={(event) => {
                  const { checked } = event.target;
                  setDraft((current) => ({ ...current, geo_checkin_enabled: checked }));
                }}
              />
              <span>{t('geo.policy.enabled')}</span>
            </label>
          </div>

          <div className="wp-field">
            <label className="wp-field__label" htmlFor="w10-policy-accuracy">
              {t('geo.policy.maxAccuracy')}
            </label>
            <input
              className="wp-field__input"
              id="w10-policy-accuracy"
              data-testid="w10-policy-accuracy"
              type="number"
              min={5}
              max={1000}
              value={maxAccuracyM}
              onChange={(event) => {
                const next = Number(event.target.value);
                setDraft((current) => ({ ...current, max_accuracy_m: next }));
              }}
            />
            <p className="wp-small wp-muted">{t('geo.policy.maxAccuracy.hint')}</p>
          </div>

          <div className="wp-field">
            <label className="wp-field__label" htmlFor="w10-policy-travel">
              {t('geo.policy.maxTravel')}
            </label>
            <input
              className="wp-field__input"
              id="w10-policy-travel"
              data-testid="w10-policy-travel"
              type="number"
              min={50}
              max={2000}
              value={maxTravelKmh}
              onChange={(event) => {
                const next = Number(event.target.value);
                setDraft((current) => ({ ...current, max_travel_kmh: next }));
              }}
            />
            <p className="wp-small wp-muted">{t('geo.policy.maxTravel.hint')}</p>
          </div>

          <fieldset className="wp-filters__group" data-testid="w10-policy-mode">
            <legend>{t('geo.policy.mode.legend')}</legend>
            <label>
              <input
                type="radio"
                name="w10-policy-mode"
                data-testid="w10-policy-mode-allow"
                checked={mode === 'allow_flag'}
                onChange={() => {
                  setDraft((current) => ({ ...current, outside_geofence_mode: 'allow_flag' }));
                }}
              />
              {t('geo.policy.mode.allow_flag')}
            </label>
            <p className="wp-small wp-muted">{t('geo.policy.mode.allow_flag.note')}</p>
            <label>
              <input
                type="radio"
                name="w10-policy-mode"
                data-testid="w10-policy-mode-block"
                checked={mode === 'block'}
                onChange={() => {
                  setDraft((current) => ({ ...current, outside_geofence_mode: 'block' }));
                }}
              />
              {t('geo.policy.mode.block')}
            </label>
            <p className="wp-small wp-muted">{t('geo.policy.mode.block.note')}</p>
          </fieldset>

          <p className="wp-small wp-muted" data-testid="w10-policy-updated">
            {props.policy.updated_at === null
              ? t('geo.policy.updated.never')
              : t('geo.policy.updated.at', {
                  at: formatInstant(props.policy.updated_at, props.locale, props.timezone),
                  by: props.policy.updated_by ?? t('geo.policy.updated.unknown'),
                })}
          </p>

          <div className="wp-filters__actions">
            <button
              type="submit"
              className="wp-button wp-button--primary"
              data-testid="w10-policy-save"
              disabled={!dirty || props.savePending}
            >
              {props.savePending ? t('geo.policy.saving') : t('geo.policy.save')}
            </button>
          </div>

          {props.saveError === null ? null : (
            <ApiErrorNote
              error={props.saveError}
              subject={t('geo.policy.subject')}
              testId="w10-policy-save-error"
            />
          )}

          {props.saved === null || dirty ? null : (
            <StatusNote severity="low" title={t('geo.policy.saveOk')} testId="w10-policy-save-ok" />
          )}
        </form>
      )}
    </section>
  );
}
